import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Upload, X, FileText, Send, RefreshCw, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { api, API, loadAuthToken, setAuthToken } from "../lib/api";
import { SERVICES } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";

const STATUS_BADGE = {
  "Received": "bg-slate-100 text-slate-800",
  "Documents Checking": "bg-yellow-100 text-yellow-900",
  "In Process": "bg-blue-100 text-blue-800",
  "Completed": "bg-green-100 text-green-800",
  "Rejected": "bg-red-100 text-red-800",
  "Transferred": "bg-purple-100 text-purple-800",
  "Assigned": "bg-indigo-100 text-indigo-800",
};

const acceptFor = (a) => {
  if (a === "photo") return ".jpg,.jpeg,.png,image/jpeg,image/png";
  if (a === "pdf") return ".pdf,application/pdf";
  return ".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf";
};

const BranchDashboardPage = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [branches, setBranches] = useState([]);
  const [dbServices, setDbServices] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", mobile: "", email: "", address: "",
    service_key: "", service_name: "",
    custom_work: "", notes: "",
  });
  const [dynFields, setDynFields] = useState({});
  const [dynFiles, setDynFiles] = useState({});
  const [files, setFiles] = useState([]);
  const [needInfoLead, setNeedInfoLead] = useState(null);
  const fileRef = useRef(null);

  const activeService = useMemo(
    () => dbServices.find((s) => s.key === form.service_key)
      || SERVICES.find((s) => s.key === form.service_key)
      || null,
    [form.service_key, dbServices]
  );

  useEffect(() => {
    if (!loadAuthToken()) { navigate("/admin/login"); return; }
    const stored = localStorage.getItem("ap_admin_user");
    if (stored) setMe(JSON.parse(stored));
    load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [l, b, s] = await Promise.all([
        api.get("/branch/leads"),
        api.get("/admin/branches"),
        api.get("/branch/services"),
      ]);
      setLeads(l.data || []);
      setBranches(b.data || []);
      setDbServices(s.data || []);
      // Default select first service if none selected
      if (!form.service_key) {
        const first = (s.data && s.data[0]) || SERVICES[0];
        setForm((f) => ({ ...f, service_key: first.key, service_name: first.name_en || first.en?.title || "" }));
      }
    } catch (err) {
      if (err?.response?.status === 401) navigate("/admin/login");
      else toast.error(err?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const handleFiles = (list) => {
    const arr = Array.from(list || []);
    const valid = arr.filter((f) => {
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      const ok = ["jpg", "jpeg", "png", "pdf"].includes(ext) && f.size <= 15 * 1024 * 1024;
      if (!ok) toast.error(`${f.name}: not allowed`);
      return ok;
    });
    setFiles((prev) => [...prev, ...valid]);
  };

  const setService = (key) => {
    const s = dbServices.find((x) => x.key === key) || SERVICES.find((x) => x.key === key);
    setForm({ ...form, service_key: key, service_name: key === "other" ? "Other / General Request" : (s?.name_en || s?.en?.title || key) });
    setDynFields({}); setDynFiles({});
  };

  const visibleDynFields = useMemo(() => {
    const schema = activeService?.form_schema || [];
    return schema.filter((f) => !f.condition_field || String(dynFields[f.condition_field] || "").toLowerCase() === String(f.condition_equals || "").toLowerCase());
  }, [activeService, dynFields]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim() || !form.address.trim()) {
      toast.error("Name, mobile and address required"); return;
    }
    if (form.service_key === "other" && !(form.custom_work || "").trim()) {
      toast.error("Kya kaam hai — likhna zaroori hai"); return;
    }
    // Enforce required dynamic fields
    for (const f of visibleDynFields) {
      if (f.required) {
        const has = f.type === "file" ? dynFiles[f.key] : dynFields[f.key];
        if (!has || (typeof has === "string" && !has.trim())) {
          toast.error(`Required: ${f.label_en}`); return;
        }
      }
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (Object.keys(dynFields).length > 0) fd.append("dynamic_fields", JSON.stringify(dynFields));
      files.forEach((f) => fd.append("files", f));
      Object.entries(dynFiles).forEach(([key, f]) => {
        if (f instanceof File) {
          const renamed = new File([f], `${key}__${f.name}`, { type: f.type });
          fd.append("files", renamed);
        }
      });
      const res = await api.post("/branch/leads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Sent to Central Office — Tracking ID: ${res.data.request_id}`);
      setForm({ ...form, name: "", mobile: "", email: "", address: "", custom_work: "", notes: "" });
      setDynFields({}); setDynFiles({}); setFiles([]);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Submit failed"); }
    finally { setSubmitting(false); }
  };

  const logout = () => { setAuthToken(null); localStorage.removeItem("ap_admin_user"); navigate("/admin/login"); };
  const branchName = me && branches.find((b) => b.id === me.branch_id)?.name_en;

  return (
    <div data-testid="branch-dashboard" className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-10">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.14em] text-teal-700 font-semibold">Branch Operator</div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-heading font-semibold text-slate-900">{branchName || "Branch Dashboard"}</h1>
          {me && <div className="text-sm text-slate-500">{me.name} · {me.email}</div>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/admin/accounts")} data-testid="branch-nav-accounts-btn">हिसाब-किताब</Button>
          <Button variant="outline" onClick={() => navigate("/admin/customers")} data-testid="branch-nav-customers-btn">Customer Details</Button>
          <Button variant="outline" onClick={logout} data-testid="branch-logout-btn"><LogOut className="w-4 h-4 mr-2" /> Sign out</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Internal request form */}
        <div className="lg:col-span-2">
          <div className="rounded-xl bg-white border border-slate-200 p-6 sticky top-24">
            <h2 className="font-heading text-xl font-semibold text-slate-900">New Internal Request</h2>
            <p className="mt-1 text-sm text-slate-600">Fill on behalf of customer. Sent to Central Office with your branch info auto-attached.</p>
            <form onSubmit={submit} className="mt-5 space-y-3 text-sm">
              <div>
                <Label>Service *</Label>
                <Select value={form.service_key} onValueChange={setService}>
                  <SelectTrigger data-testid="branch-form-service"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {(dbServices.length > 0 ? dbServices : SERVICES).filter((s) => s.key !== "other").map((s) => (
                      <SelectItem key={s.key} value={s.key} data-testid={`branch-svc-${s.key}`}>
                        {s.name_en || s.en?.title}
                      </SelectItem>
                    ))}
                    <SelectItem value="other" data-testid="branch-svc-other">Other / General Request (nayi service)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Service header photo + required docs */}
              {activeService && (activeService.photo_file || activeService.required_docs?.length > 0 || activeService.fee) && (
                <div className="rounded-md bg-teal-50 border border-teal-200 p-3">
                  {activeService.photo_file && (
                    <img src={`${API}/services/${activeService.key}/photo`} alt="" className="w-full h-24 object-cover rounded-md mb-2" />
                  )}
                  {activeService.fee && <div className="text-xs text-slate-700 mb-1"><b>Fee:</b> {activeService.fee}</div>}
                  {(activeService.required_docs || []).length > 0 && (
                    <>
                      <div className="text-xs font-semibold text-teal-700 uppercase tracking-widest mb-1 flex items-center gap-1"><ClipboardCheck className="w-3 h-3" /> Required docs</div>
                      <ul className="text-xs text-slate-800 list-disc ml-4 space-y-0.5">
                        {activeService.required_docs.map((d, i) => <li key={i}>{d}</li>)}
                      </ul>
                    </>
                  )}
                </div>
              )}

              <div><Label>Customer Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="branch-form-name" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Mobile *</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="branch-form-mobile" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              </div>
              <div><Label>Address *</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="branch-form-address" /></div>

              {form.service_key === "other" && (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3 space-y-1">
                  <Label>Kya kaam hai? (service ka naam + details) *</Label>
                  <Textarea rows={3} value={form.custom_work} onChange={(e) => setForm({ ...form, custom_work: e.target.value })} placeholder="e.g. Bihar Labour Card banana hai, customer ke paas Aadhaar aur bank passbook hai" data-testid="branch-form-custom-work" />
                  <div className="text-xs text-slate-600">Ye request Central team ke paas jayegi. Jo bhi note likhna ho niche Notes me bhi likh sakte hain.</div>
                </div>
              )}

              {/* Dynamic fields from service schema */}
              {visibleDynFields.length > 0 && (
                <div className="rounded-md border border-slate-200 p-3 space-y-3 bg-slate-50/40">
                  <div className="text-xs uppercase tracking-widest text-teal-700 font-semibold">{activeService.name_en} — extra details</div>
                  {visibleDynFields.map((f) => (
                    <div key={f.key}>
                      <Label className="text-sm">{f.label_en} {f.required && <span className="text-orange-600">*</span>}</Label>
                      <div className="mt-1">
                        {f.type === "textarea" ? (
                          <Textarea rows={2} value={dynFields[f.key] || ""} onChange={(e) => setDynFields({ ...dynFields, [f.key]: e.target.value })} data-testid={`branch-dyn-${f.key}`} />
                        ) : f.type === "dropdown" ? (
                          <Select value={dynFields[f.key] || ""} onValueChange={(v) => setDynFields({ ...dynFields, [f.key]: v })}>
                            <SelectTrigger data-testid={`branch-dyn-${f.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                            <SelectContent>{(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : f.type === "checkbox" ? (
                          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!dynFields[f.key]} onChange={(e) => setDynFields({ ...dynFields, [f.key]: e.target.checked })} data-testid={`branch-dyn-${f.key}`} /> Yes</label>
                        ) : f.type === "file" ? (
                          <div>
                            <input type="file" accept={acceptFor(f.accept)} onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              setDynFiles({ ...dynFiles, [f.key]: file });
                              setDynFields({ ...dynFields, [f.key]: file.name });
                            }} data-testid={`branch-dyn-${f.key}`} className="text-sm" />
                            {dynFiles[f.key] && <div className="mt-1 text-xs text-slate-500">{dynFiles[f.key].name}</div>}
                            <div className="mt-1 text-xs text-slate-500">{f.accept === "photo" ? "Photo only" : f.accept === "pdf" ? "PDF only" : "Photo or PDF"}</div>
                          </div>
                        ) : (
                          <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={dynFields[f.key] || ""} onChange={(e) => setDynFields({ ...dynFields, [f.key]: e.target.value })} data-testid={`branch-dyn-${f.key}`} />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="branch-form-notes" /></div>

              <div>
                <Label>Documents</Label>
                <div className="mt-1 rounded-md border-2 border-dashed border-slate-300 bg-slate-50 p-4 text-center cursor-pointer" onClick={() => fileRef.current?.click()}>
                  <Upload className="w-5 h-5 mx-auto text-slate-500" />
                  <div className="mt-1 text-xs text-slate-600">Click to add JPG/PNG/PDF</div>
                  <input ref={fileRef} type="file" multiple accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="branch-form-files" />
                </div>
                {files.length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs">
                    {files.map((f, i) => (
                      <li key={i} className="flex items-center gap-1 text-slate-600">
                        <FileText className="w-3 h-3" /> {f.name}
                        <button type="button" onClick={() => setFiles(files.filter((_, x) => x !== i))} className="ml-auto"><X className="w-3 h-3" /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <Button type="submit" disabled={submitting} className="w-full h-11 bg-teal-700 hover:bg-teal-800 text-white" data-testid="branch-form-submit">
                <Send className="w-4 h-4 mr-2" /> {submitting ? "Sending…" : "Send to Central Office"}
              </Button>
            </form>
          </div>
        </div>

        {/* Branch leads list */}
        <div className="lg:col-span-3">
          <div className="rounded-lg bg-white border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="font-heading font-semibold">Requests from this branch</h3>
              <Button variant="outline" size="sm" onClick={load}><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">Tracking ID</th>
                    <th className="px-4 py-3 font-medium">Customer</th>
                    <th className="px-4 py-3 font-medium">Service</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>}
                  {!loading && leads.length === 0 && <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-500">No requests yet.</td></tr>}
                  {!loading && leads.map((l) => (
                    <tr key={l.id} className={`border-t border-slate-100 hover:bg-slate-50 ${l.need_info?.active ? "bg-red-50/60" : ""}`} data-testid={`branch-lead-${l.request_id}`}>
                      <td className="px-4 py-3 font-medium cursor-pointer" onClick={() => navigate(`/workspace/${l.id}`)}>{l.request_id}{l.need_info?.active && <span className="ml-2 inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" title="Info Needed" />}</td>
                      <td className="px-4 py-3 cursor-pointer" onClick={() => navigate(`/workspace/${l.id}`)}>{l.name} · {l.mobile}</td>
                      <td className="px-4 py-3 cursor-pointer" onClick={() => navigate(`/workspace/${l.id}`)}>{l.service_name}</td>
                      <td className="px-4 py-3">
                        <Badge className={STATUS_BADGE[l.status]}>{l.status}</Badge>
                        {l.need_info?.active && (
                          <button
                            onClick={() => setNeedInfoLead(l)}
                            className="ml-1 inline-flex items-center gap-1 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-medium px-2 py-0.5 animate-pulse"
                            data-testid={`open-need-info-${l.request_id}`}
                          >
                            📄 Upload / Reply
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap cursor-pointer" onClick={() => navigate(`/workspace/${l.id}`)}>{new Date(l.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <BranchNeedInfoDialog lead={needInfoLead} onClose={() => setNeedInfoLead(null)} onDone={load} />
    </div>
  );
};

const BranchNeedInfoDialog = ({ lead, onClose, onDone }) => {
  const [answers, setAnswers] = useState({});
  const [pickedFiles, setPickedFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const inp = useRef(null);

  if (!lead) return null;
  const ni = lead.need_info || {};

  const submit = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("request_id", lead.request_id);
      fd.append("mobile", lead.mobile);
      if (Object.keys(answers).length) fd.append("answers", JSON.stringify(answers));
      pickedFiles.forEach((f) => fd.append("files", f));
      await api.post("/track/need-info/fill", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sent to central office");
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="branch-need-info-dialog">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b bg-red-50">
          <div className="text-xs uppercase tracking-widest text-red-700 font-semibold">Central Office needs more info</div>
          <div className="mt-1 text-lg font-heading font-semibold text-slate-900">{lead.request_id} · {lead.name}</div>
          <p className="mt-2 text-sm text-slate-800 whitespace-pre-wrap"><b>Message:</b> {ni.message}</p>
        </div>

        <div className="p-6 space-y-3 text-sm">
          {(ni.fields || []).map((f) => (
            <div key={f.key}>
              <Label className="text-sm">{f.label}</Label>
              <Input value={answers[f.key] || ""} onChange={(e) => setAnswers({ ...answers, [f.key]: e.target.value })} data-testid={`branch-ni-${f.key}`} />
            </div>
          ))}

          <div>
            <Label className="text-sm">Upload additional documents</Label>
            <div className="mt-1 rounded-md border-2 border-dashed border-slate-300 bg-slate-50 p-4 text-center cursor-pointer" onClick={() => inp.current?.click()}>
              <Upload className="w-5 h-5 mx-auto text-slate-500" />
              <div className="mt-1 text-xs text-slate-600">Click to add JPG/PNG/PDF</div>
              <input ref={inp} type="file" multiple accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={(e) => setPickedFiles([...pickedFiles, ...Array.from(e.target.files || [])])} data-testid="branch-ni-files" />
            </div>
            {pickedFiles.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs">
                {pickedFiles.map((f, i) => (
                  <li key={i} className="flex items-center gap-1 text-slate-600">
                    <FileText className="w-3 h-3" /> {f.name}
                    <button type="button" onClick={() => setPickedFiles(pickedFiles.filter((_, x) => x !== i))} className="ml-auto"><X className="w-3 h-3" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="p-4 border-t flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white" data-testid="branch-ni-submit"><Send className="w-4 h-4 mr-2" /> {saving ? "Sending…" : "Send to Central"}</Button>
        </div>
      </div>
    </div>
  );
};

export default BranchDashboardPage;
